import { useEffect, useMemo, useRef, useState } from 'react';
import useSound from '@mrmartineau/use-sound';
import CardVariantOverlay from '../CardVariantOverlay';
import { consumeDuplicateCards, getBaseCard, getCardVariant, getDeckCost, getTotalSpendableDuplicates, humanCardName, rollDeckRewards, VARIANT_NAMES } from '../utils/cards';
import { SECRET_DECKS } from '../utils/secretDecks';
import { getDeck, KEYS } from '../utils/storage';
import openingSxf from '../sounds/wash.mp3';
import openedSxf from '../sounds/magic.mp3';

const getOpenedSecretDecks = () => {
  try {
    return JSON.parse(localStorage.getItem(KEYS.SECRET_DECKS_OPENED) || '{}');
  } catch {
    return {};
  }
};

export function SecretDeckVaultModal({ isOpen, onClose, onDeckOpened, filterVariant = null, cardImageFolder = 'smallcards' }) {
  const [statusByDeck, setStatusByDeck] = useState({});
  const [isOpeningDeckId, setIsOpeningDeckId] = useState(null);
  const [deck, setDeck] = useState(() => getDeck());
  const [duplicateBalance, setDuplicateBalance] = useState(() => getTotalSpendableDuplicates(deck));
  const [openedDecksById, setOpenedDecksById] = useState(() => getOpenedSecretDecks());
  const [openedCardsByDeck, setOpenedCardsByDeck] = useState(() => {
    const persisted = getOpenedSecretDecks();
    return Object.fromEntries(
      Object.entries(persisted)
        .map(([deckId, value]) => [deckId, Array.isArray(value?.rewards) ? value.rewards : []])
    );
  });
  const openingTimeoutRef = useRef(null);
  const [playOpeningSound] = useSound(openingSxf);
  const [playOpenedSound] = useSound(openedSxf);

  useEffect(() => {
    return () => {
      if (openingTimeoutRef.current) {
        clearTimeout(openingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setDeck(getDeck());
      setDuplicateBalance(getTotalSpendableDuplicates(getDeck()));
      setOpenedDecksById(getOpenedSecretDecks());
    }
  }, [isOpen]);

  const deckDefs = useMemo(() => SECRET_DECKS, []);
  const visibleDeckDefs = useMemo(() => {
    if (!filterVariant) return deckDefs;
    return deckDefs.filter((deck) => deck.variant === filterVariant);
  }, [deckDefs, filterVariant]);

  const renderCard = (card, key, className = 'card') => {
    const variant = getCardVariant(card);
    const baseCard = getBaseCard(card).toLowerCase();

    return (
      <div className={`${className} ${variant ?? ''}`} key={key}>
        {variant ? (
          <CardVariantOverlay
            variant={variant}
            imageSrc={`./assets/${cardImageFolder}/${baseCard}.png`}
            alt={card}
            title={humanCardName(card)}
            className='front'
            animated={false}
          />
        ) : (
          <img alt={card} className='front' src={`./assets/${cardImageFolder}/${baseCard}.png`} />
        )}
      </div>
    );
  };

  const openSecretDeck = (deckDef) => {
    if (isOpeningDeckId) return;

    if (openedDecksById[deckDef.id]) {
      setStatusByDeck((prev) => ({
        ...prev,
        [deckDef.id]: { type: 'error', text: 'This deck has already been opened.' },
      }));
      return;
    }

    const currentDeck = getDeck();
    const availableDuplicates = getTotalSpendableDuplicates(currentDeck);
    const deckCost = getDeckCost(currentDeck, deckDef.variant);
    const paymentCount = deckCost;

    if (availableDuplicates < deckCost) {
      setStatusByDeck((prev) => ({
        ...prev,
        [deckDef.id]: { type: 'error', text: 'Insufficient duplicates' },
      }));
      return;
    }

    setIsOpeningDeckId(deckDef.id);
    playOpeningSound();

    openingTimeoutRef.current = setTimeout(() => {
      const rewards = rollDeckRewards(deckDef);

      // Payment is taken from the deck as it stood before the pull, so a card that arrives
      // in this batch of rewards can never be one of the duplicates spent to buy it.
      const { nextDeck: paidDeck, paidCards } = consumeDuplicateCards(currentDeck, paymentCount);
      const nextDeck = { ...paidDeck };
      rewards.forEach((card) => {
        nextDeck[card] = (nextDeck[card] || 0) + 1;
      });
      localStorage.setItem(KEYS.DECK, JSON.stringify(nextDeck));
      setDeck(nextDeck);
      setDuplicateBalance(getTotalSpendableDuplicates(nextDeck));

      const nextOpenedDecks = {
        ...openedDecksById,
        [deckDef.id]: {
          openedAt: Date.now(),
          rewards,
          paidCards,
        },
      };
      setOpenedDecksById(nextOpenedDecks);
      localStorage.setItem(KEYS.SECRET_DECKS_OPENED, JSON.stringify(nextOpenedDecks));

      setOpenedCardsByDeck((prev) => ({ ...prev, [deckDef.id]: rewards }));
      setStatusByDeck((prev) => ({
        ...prev,
        [deckDef.id]: { type: 'success', text: `Paid ${paidCards.length} duplicate${paidCards.length === 1 ? '' : 's'}, opened ${deckDef.rewardCount}. This deck is now consumed.` },
      }));
      playOpenedSound();
      setIsOpeningDeckId(null);
      openingTimeoutRef.current = null;
      onDeckOpened?.(rewards);
    }, 1500);
  };

  const renderOpenedSummary = (deckDef, openedCards) => {
    const openedMeta = openedDecksById[deckDef.id];
    const paidCards = Array.isArray(openedMeta?.paidCards) ? openedMeta.paidCards : [];

    return (
      <>
        <div className='secret-deck-opened-head'>
          <span className='secret-deck-badge'>Opened For Today</span>
        </div>

        {paidCards.length > 0 && (
          <>
            <p style={{ textAlign: 'center', borderTop: '1px solid rgba(255, 255, 255,0.2)', paddingTop: '0.5em' }}><small>Duplicates Spent</small></p>
            <div className='example'>
              <div className='example-top example-top-center'>
                <div className='grid'>
                  {paidCards.map((card, i) => renderCard(card, `${deckDef.id}_paid_${i}`))}
                </div>
              </div>
            </div>
          </>
        )}

        <p style={{ textAlign: 'center' }}><small>Pulled</small></p>
        <div className='example'>
          <div className='example-top example-top-center'>
            <div className='grid'>
              {openedCards.slice(0, deckDef.rewardCount).map((card, i) => renderCard(card, `${deckDef.id}_opened_${i}`))}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={`modal secret-deck-modal ${isOpen ? 'open' : 'closed'}`}>
      <div className='modal-overlay' onClick={onClose}></div>
      <div className='modal-content'>
        {/* Without this the "Insufficient duplicates" button gives the player no way to
            tell how close they are, since the collection screen counts whole cards. */}
        <p className='secret-deck-balance'>
          <small>
            You have <strong>{duplicateBalance}</strong> spare duplicate{duplicateBalance === 1 ? '' : 's'} to trade in.
            {' '}Your last copy of a card is never spent.
          </small>
        </p>
        <div className='secret-deck-grid'>
          {visibleDeckDefs.map((deckDef) => {
            const openedCards = openedCardsByDeck[deckDef.id] || [];
            const status = statusByDeck[deckDef.id];
            const alreadyOpened = Boolean(openedDecksById[deckDef.id]);
            const isOpeningThisDeck = isOpeningDeckId === deckDef.id;

            return (
              <div className='secret-deck-box' key={deckDef.id}>
                <div className='secret-deck-header'>
                  <div className={`card booster secret-deck-pack ${isOpeningThisDeck ? 'opening' : ''}`}>
                    <CardVariantOverlay
                      variant={deckDef.variant}
                      imageSrc='./deck.png'
                      alt={deckDef.name}
                      title={deckDef.name}
                      className='front'
                    />
                  </div>
                  <div>
                    <h3>{deckDef.name}</h3>
                    {(() => {
                      const deckCost = getDeckCost(deck, deckDef.variant);
                      return (
                        <>
                          <p><small><em>{deckDef.description}</em></small></p>
                          <p><small>{Math.round((deckDef.themedRate || 0) * 100)}% minimum {VARIANT_NAMES[deckDef.variant] || deckDef.variant} rate.</small></p>
                          <p><small>Costs {deckCost} random duplicates for {deckDef.rewardCount} new cards.</small></p>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {alreadyOpened ? (
                  renderOpenedSummary(deckDef, openedCards)
                ) : (
                  <>
                    <div className='secret-deck-controls'>
                      {(() => {
                        const deckCost = getDeckCost(deck, deckDef.variant);
                        const canAffordDeck = duplicateBalance >= deckCost;
                        return (
                          <button onClick={() => openSecretDeck(deckDef)} disabled={Boolean(isOpeningDeckId) || !canAffordDeck}>
                            {isOpeningThisDeck
                              ? 'Opening...'
                              : canAffordDeck
                                ? `Pay ${deckCost} Random Duplicate${deckCost === 1 ? '' : 's'} & Open`
                                : 'Insufficient duplicates'}
                          </button>
                        );
                      })()}
                    </div>

                    {status ? (
                      <p className={`secret-deck-status ${status.type}`}><small>{status.text}</small></p>
                    ) : null}
                  </>
                )}
              </div>
            );
          })}

          {visibleDeckDefs.length === 0 && (
            <div className='secret-deck-box'>
              <p><small>No themed deck is available for this card yet.</small></p>
            </div>
          )}
        </div>
        <div className='modal-buttons secret-deck-vault-exit'>
          <button onClick={onClose}>Back</button>
        </div>
      </div>
      {isOpeningDeckId && <div className='booster-opening-overlay'></div>}
    </div>
  );
}
